import { useTranslation } from '@klv/hooks/useTranslation';

import { convertPadding } from '@klv/atoms/custom-padding/utils';
import Button from '@klv/atoms/button';
import BlogCard from '@klv/molecules/blog-card';
import TwoColumnWithNewsfeed from '../two-column-with-newsfeed';

import { getColorVariantFromBackground, convertNamedColorToColorVariant, generateClassName } from '@klv/shared/utils/functions';

import * as styles from './styles/blogCards.module.scss';

import type { IBlogCards } from './types';

const ConditionalWrapper = ({
  condition,
  wrapper,
  children,
}: {
  condition: boolean;
  wrapper: (children: React.ReactNode) => JSX.Element;
  children: JSX.Element;
}) => condition ? wrapper(children) : children;

const BlogCards = ({
  backgroundColor,
  button,
  canShowViewAll = true,
  className,
  customPadding,
  category,
  displayGrayLine = true,
  headline,
  newsfeed,
  posts,
  postSource = 'category',
  subhead,
  withNewsfeed = false,
  cardTitleSize,
  ...props
}: IBlogCards) => {
  const { t } = useTranslation();
  const { name = '', link, posts: categoryPosts } = category || {};
  const categoryPostSource = Array.isArray(categoryPosts) ? categoryPosts : categoryPosts?.nodes;
  if ((postSource === 'category') && categoryPostSource?.length === 0) return null;
  if ((postSource === 'latest' || postSource === 'manual') && posts?.length === 0) return null;

  const cardPosts = ((postSource === 'category') ? categoryPostSource : posts) || [];
  const backgroundVariant = convertNamedColorToColorVariant(backgroundColor);

  const paddingTop = customPadding?.customPaddingTop ? convertPadding(customPadding.customPaddingTop) : 'md';
  const paddingBottom = customPadding?.customPaddingBottom ? convertPadding(customPadding.customPaddingBottom) : 'md';

  return (
    <div
      className={generateClassName([
        'blogCards',
        className,
        styles.blogCards,
        styles[`paddingTop${paddingTop}`],
        styles[`paddingBottom${paddingBottom}`],
        styles[backgroundVariant],
        backgroundColor === 'white' && styles.white,
      ])}
      {...props}
    >
      <div className={generateClassName(['grid', styles.wrapper])}>
        <div className={generateClassName([styles.borderContainer, displayGrayLine && styles.displayGrayLine])}>
          <ConditionalWrapper
            condition={withNewsfeed}
            wrapper={(children) => (
              <TwoColumnWithNewsfeed
                backgroundColor={backgroundVariant}
                newsfeed={newsfeed!}
              >
                {children}
              </TwoColumnWithNewsfeed>
            )}
          >
            <>
              <div className={styles.titleRow}>
                <div className={styles.text}>
                  <h2
                    className={generateClassName([styles.categoryTitle, 'h3', 'semiBold'])}
                  >
                    { headline || name }
                  </h2>

                  {subhead && (
                    <div
                      className={generateClassName(['p2'])}
                    >
                      {subhead}
                    </div>
                  )}
                </div>
                {canShowViewAll && (postSource === 'category') && (
                  <Button
                    buttonColor={getColorVariantFromBackground(backgroundColor)}
                    className={styles.button}
                    openInNewTab={button?.openInNewTab}
                    text={button?.text ? button.text : t('organisms_blogCards_viewCategory')}
                    url={link}
                    variant="tertiary"
                  />
                )}
                {canShowViewAll && (postSource === 'latest') && (
                  <Button
                    buttonColor={getColorVariantFromBackground(backgroundColor)}
                    className={styles.button}
                    openInNewTab={button?.openInNewTab}
                    text={button?.text ? button.text : t('organisms_blogCards_viewAll')}
                    url={`${t('langCodes_pathPrefix') !== '' ? `/${t('langCodes_pathPrefix')}` : ''}/blog/category/all-articles`}
                    variant="tertiary"
                  />
                )}
              </div>

              <div className={generateClassName(['grid', styles.cardContainer])}>
                {(cardPosts?.length > 0) && cardPosts.map(({
                  title, uri, date, excerpt, featuredImage, categories, templatesEditorial,
                }, index) => {
                  if (withNewsfeed && index > 1) {
                    return null;
                  }
                  const { postTitle: { shortTitle = '' }, cardImage } = templatesEditorial || {};
                  const primaryCategory = categories?.nodes?.filter(({ slug }) => slug !== 'whats-new')?.[0] || {
                    name: '',
                    slug: '',
                    uri: '',
                    link: '',
                  };
                  return (
                    <BlogCard
                      key={title}
                      title={shortTitle || title}
                      categoryName={(postSource === 'category') ? name : primaryCategory?.name}
                      categoryLink={(postSource === 'category') ? link : primaryCategory?.link}
                      className={styles.blogCard}
                      uri={uri}
                      date={date}
                      excerpt={excerpt}
                      featuredImage={cardImage || featuredImage?.node}
                      backgroundColor={backgroundVariant}
                      cardTitleSize={cardTitleSize}
                    />
                  );
                })}
              </div>
            </>
          </ConditionalWrapper>
        </div>
      </div>
    </div>
  );
};

export default BlogCards;
